import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Send, Loader2 } from "lucide-react";
import { API_ENDPOINTS } from "@/config/apiEndpoints";

// Converte qualquer valor (número ou string) para string formatada com casas decimais
const fmt = (val, dec = 0) => Number(val ?? 0).toFixed(dec);

export function WeeklyReportTab({ authAxios }) {
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { authAxiosRef.current.get(API_ENDPOINTS.intelligence.weeklyReport).then(res => setReport(res.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  const handleSend = async () => { setSending(true); try { const res = await authAxios.post("/intelligence/weekly-report/send"); if (res.data.success) { const parts = []; if (res.data.whatsapp_sent > 0) parts.push(`${res.data.whatsapp_sent} WhatsApp`); if (res.data.email_sent > 0) parts.push(`${res.data.email_sent} email(s)`); toast.success(`Relatório enviado: ${parts.join(", ")}`); } else { toast.error("Nenhum destinatário conectado"); } } catch { toast.error("Erro ao enviar relatório"); } setSending(false); };
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!report) return <div className="text-center py-12 text-gray-400 text-sm">Sem dados para o relatório desta semana.</div>;

  // Suporta estrutura v2 {revenue: {this_week, last_week, growth}} e legacy {revenue: number}
  const revenueThisWeek = typeof report.revenue === "object" ? report.revenue?.this_week : report.revenue;
  const revenueGrowth = typeof report.revenue === "object" ? report.revenue?.growth : report.revenue_growth;
  const ordersThisWeek = typeof report.orders === "object" ? report.orders?.this_week : report.orders;
  const ordersGrowth = typeof report.orders === "object" ? report.orders?.growth : null;
  const ticketMedio = typeof report.ticket_medio === "object" ? report.ticket_medio?.this_week : report.ticket_medio;
  const growthSign = Number(revenueGrowth ?? 0) >= 0 ? "+" : "";

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="bg-gray-800 px-5 py-3">
          <h2 className="text-sm font-bold text-white">Relatório Executivo — Últimos 7 dias</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-gray-100">
          <div className="p-4">
            <p className="text-xs text-gray-400 uppercase tracking-wide">Receita</p>
            <p className="text-2xl font-bold text-gray-800" data-testid="weekly-revenue">{fmt(revenueThisWeek)}€</p>
            {revenueGrowth != null && <p className={`text-xs font-semibold mt-0.5 ${Number(revenueGrowth) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{growthSign}{revenueGrowth}% vs semana ant.</p>}
          </div>
          <div className="p-4"><p className="text-xs text-gray-400 uppercase tracking-wide">Pedidos</p><p className="text-2xl font-bold text-gray-800">{ordersThisWeek}</p>{ordersGrowth != null && <p className={`text-xs font-semibold mt-0.5 ${Number(ordersGrowth) >= 0 ? "text-emerald-600" : "text-red-600"}`}>{Number(ordersGrowth) >= 0 ? "+" : ""}{ordersGrowth}%</p>}</div>
          <div className="p-4"><p className="text-xs text-gray-400 uppercase tracking-wide">Ticket Médio</p><p className="text-2xl font-bold text-gray-800">{fmt(ticketMedio, 2)}€</p></div>
          <div className="p-4"><p className="text-xs text-gray-400 uppercase tracking-wide">Semana anterior</p><p className="text-2xl font-bold text-gray-600">{typeof report.revenue === "object" ? fmt(report.revenue?.last_week) : "—"}€</p></div>
        </div>
      </div>

      <Button onClick={handleSend} disabled={sending} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="send-weekly-report-btn">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Enviar relatório por WhatsApp + Email
      </Button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Mais vendidos / lucrativos — v2 usa top_products, legacy usa top_sold/top_profitable */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Mais vendidos</h3></div>
          <div className="divide-y">
            {(report.top_products ?? report.top_sold?.map(([name, qty]) => ({ name, qty })) ?? [])
              .sort((a, b) => (b.qty ?? 0) - (a.qty ?? 0))
              .slice(0, 5)
              .map((p, i) => (
                <div key={i} className="flex items-center px-4 py-2">
                  <span className="w-6 text-xs text-gray-400 font-bold">#{i+1}</span>
                  <p className="flex-1 text-sm">{p.name}</p>
                  <p className="text-sm font-bold">{p.qty}x</p>
                </div>
              ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Mais lucrativos</h3></div>
          <div className="divide-y">
            {(report.top_products ?? report.top_profitable?.map(([name, revenue]) => ({ name, revenue })) ?? [])
              .sort((a, b) => (b.revenue ?? 0) - (a.revenue ?? 0))
              .slice(0, 5)
              .map((p, i) => (
                <div key={i} className="flex items-center px-4 py-2">
                  <span className="w-6 text-xs text-gray-400 font-bold">#{i+1}</span>
                  <p className="flex-1 text-sm">{p.name}</p>
                  <p className="text-sm font-bold text-emerald-600">{fmt(p.revenue, 2)}€</p>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Horários de pico (v2) */}
      {report.top_hours?.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Horários com mais receita</h3></div>
          <div className="divide-y">
            {report.top_hours.map((h, i) => (
              <div key={i} className="flex items-center px-4 py-2 hover:bg-gray-50">
                <p className="flex-1 text-sm font-medium">{h.hour}</p>
                <p className="text-sm text-gray-500 mr-4">{h.orders} pedidos</p>
                <p className="text-sm font-bold">{fmt(h.revenue, 2)}€</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Vendas por dia (legacy) */}
      {report.daily && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Vendas por dia</h3></div>
          <div className="divide-y">
            {Object.entries(report.daily).map(([day, vals]) => (
              <div key={day} className="flex items-center px-4 py-2 hover:bg-gray-50">
                <p className="flex-1 text-sm">{day}</p>
                <p className="text-sm text-gray-500 mr-4">{vals.orders} pedidos</p>
                <p className="text-sm font-bold">{fmt(vals.revenue, 2)}€</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Canais (v2) */}
      {report.sources && Object.keys(report.sources).length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-bold text-sm text-gray-700">Receita por canal</h3></div>
          <div className="divide-y">
            {Object.entries(report.sources).map(([src, vals]) => (
              <div key={src} className="flex items-center px-4 py-2 hover:bg-gray-50">
                <p className="flex-1 text-sm font-medium capitalize">{src}</p>
                <p className="text-sm text-gray-500 mr-4">{vals.orders} pedidos</p>
                <p className="text-sm font-bold">{fmt(vals.revenue, 2)}€</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
